import type { RouteLocationNormalized } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';

/**
 * Functionality to handle navigation between different steps in the offer flow.
 */
export function useFlow(route?: RouteLocationNormalized) {
    const localeRoute = useLocaleRoute();
    const { t: $t } = useNuxtApp().$i18n;
    const router = useRouter();
    const queryClient = useQueryClient();
    const cartStore = useCartStore();
    const { product } = useProduct(route);
    const flowType = computed(() => product.value.isUpsell ? 'upsell' : 'booking');

    // Steps of current product
    const steps = computed(() => product.value.config.steps ?? []);
    // Current route
    const currentRoute = computed(() => route ?? router.currentRoute.value);
    // Current step from step config
    const currentStep = computed(() => getCurrentStep(steps.value, currentRoute.value));
    // Index of current step
    const currentStepIndex = computed(() => getCurrentStepIndex(steps.value, currentRoute.value));
    // query params to pass along to previous/next steps (Keep CP query param for dynamic routes)
    const persistentQuery = computed(() => ({ cp: currentRoute.value.query.cp }));
    // Previous step in flow
    const previousStep = computed(() => getPreviousFlowStep(steps.value, currentRoute.value));
    // Next step in flow
    const nextStep = computed(() => getNextFlowStep(steps.value, currentRoute.value));
    // Previous route in flow
    const previousRoute = computed(() => getStepRoute(previousStep.value, product.value.id, flowType.value, persistentQuery.value));
    // Next route in flow
    const nextRoute = computed(() => getStepRoute(nextStep.value, product.value.id, flowType.value, persistentQuery.value));
    // First route of this product offer flow
    const firstStepRoute = computed(() => getFirstFlowRoute(steps.value, product.value.id, flowType.value, persistentQuery.value));
    // Last route of this product offer flow
    const lastStepRoute = computed(() => getLastFlowRoute(steps.value, product.value.id, flowType.value, persistentQuery.value));

    // Route to start the flow. This is either the route of the step specified in the query param, or the first step of the flow.
    const startRoute = computed(() => {
        const r = route ?? useRoute();

        return getStepRoute(steps.value.find(s => s.routeName.includes(r.query.step as string)), product.value.id, flowType.value, persistentQuery.value)
            ?? firstStepRoute.value;
    });

    // Returns the label for the footer next button
    const nextLabel = computed(() => {
        // If a label is specified
        if (currentStep.value?.nextLabel) {
            return $t(currentStep.value.nextLabel);
        }
        // If there is no next step (for upsells last step)
        else if (nextStep.value === undefined) {
            return $t('core.buttons.toCart');
        }
        // default
        else {
            return $t('core.buttons.next');
        }
    });

    /**
     * Go to next step.
     *
     * If the next step would be the upsell-cart step (or is undefined, because for upsells themselves skip that step),
     * then FINISH the flow. That means we store the product to cart
     *
     * If the next step is a normal step, we just navigate to it.
     */
    async function proceedToNextStep() {
        if (nextStep.value === undefined) {
            await finishOfferFlow();
        }
        else {
            await navigateTo(nextRoute.value);
        }
    }

    /**
     * Finish the offer flow
     * - Clear cart queries
     * - Mark product as untouched
     * - Save product to cart store
     * - Navigate to upsell/overview step
     */
    async function finishOfferFlow() {
        const passengerStore = usePassengerStore();

        // Clear queries
        queryClient.removeQueries({ queryKey: ['cart-quote'] });
        queryClient.removeQueries({ queryKey: ['cart-quote-with-ancillaries'] });

        // Mark product as untouched when saved.
        // It stays untouched (in the product store) until user changes something.
        product.value.status.isTouched = false;

        // Save product to cart
        cartStore.saveProduct(product.value, passengerStore.passengers);

        await navigateTo(localeRoute({ name: 'checkout-checkout-1-cart' })); ;
    }

    /**
     * Cancel the upsell flow and go back to the upsell/overview step
     */
    async function cancelUpsellFlow() {
        await navigateTo(localeRoute({ name: 'checkout-checkout-1-cart' })); ;
    }

    /**
     *
     */
    function applySteps() {
        let steps: ProductFlowStep[] = [];

        if (product.value.config.type === 'P2P') {
            steps = flowP2P;
        }
        else if (product.value.config.type === 'Activity') {
            if (product.value.config.isUsingSeasonDates) {
                steps = flowActivitySeasonal;
            }
            else if (product.value.config.isRental) {
                steps = flowRental;
            }
            else if (product.value.config.categoryId !== undefined) {
                steps = flowActivityWithOptions;
            }
            else {
                steps = flowActivity;
            }
        }
        else if (product.value.config.type === 'Accommodation') {
            steps = flowAccommodation;
        }

        steps = steps.filter(step => product.value.isUpsell ? !step.skipIfUpsell : true) ?? [];

        product.value.config.steps = steps;
    }

    /**
     * Set main page layout
     * - For normal products, use default layout
     * - For upsell products, use subflow layout
     */
    function applyPageLayout() {
        if (product.value.isUpsell) {
            setPageLayout('subflow');
        }
        else {
            setPageLayout('default');
        }
    }

    return {
        steps,

        currentStep,
        currentStepIndex,
        previousStep,
        nextStep,
        previousRoute,
        nextRoute,
        nextLabel,
        firstStepRoute,
        lastStepRoute,
        startRoute,
        persistentQuery,

        proceedToNextStep,
        cancelUpsellFlow,
        applyPageLayout,
        applySteps,
    };
}
